import api from '@/api/axios';

const state = {
  downloadingFile: false,
  downloadError: null,
  uploadingFiles: {}, // 세션별 업로드 중인 파일들의 상태 추적 - sessionId: [files]
  pollingIntervals: new Map(), // 파일별 폴링 interval 관리
};

const mutations = {
  SET_DOWNLOADING(state, status) {
    state.downloadingFile = status;
  },
  SET_DOWNLOAD_ERROR(state, error) {
    state.downloadError = error;
  },
  ADD_UPLOADING_FILE(state, { sessionId, file }) {
    if (!state.uploadingFiles[sessionId]) {
      state.uploadingFiles[sessionId] = [];
    }
    state.uploadingFiles[sessionId].push(file);
  },
  UPDATE_UPLOADING_FILE(state, { sessionId, fileId, status, progress, errorMessage }) {
    if (!state.uploadingFiles[sessionId]) return;
    const fileIndex = state.uploadingFiles[sessionId].findIndex(f => f.file_id === fileId);
    if (fileIndex !== -1) {
      state.uploadingFiles[sessionId][fileIndex] = {
        ...state.uploadingFiles[sessionId][fileIndex],
        status,
        progress,
        error_message: errorMessage
      };
    }
  },
  REMOVE_UPLOADING_FILE(state, { sessionId, fileId }) {
    if (!state.uploadingFiles[sessionId]) return;
    state.uploadingFiles[sessionId] = state.uploadingFiles[sessionId].filter(f => f.file_id !== fileId);
    // 세션의 모든 파일이 제거되었으면 세션 자체를 정리
    if (state.uploadingFiles[sessionId].length === 0) {
      delete state.uploadingFiles[sessionId];
    }
  },
  SET_POLLING_INTERVAL(state, { fileId, interval }) {
    state.pollingIntervals.set(fileId, interval);
  },
  CLEAR_POLLING_INTERVAL(state, fileId) {
    const interval = state.pollingIntervals.get(fileId);
    if (interval) {
      clearInterval(interval);
      state.pollingIntervals.delete(fileId);
    }
  },
  CLEAR_ALL_POLLING_INTERVALS(state) {
    state.pollingIntervals.forEach(interval => clearInterval(interval));
    state.pollingIntervals.clear();
  }
};

const actions = {
  async uploadFile({ commit, dispatch }, { formData, sessionId }) {
    try {
      const response = await api.post('/api/v1/file/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('파일 업로드 응답:', response.data); // 디버깅용 로그
      
      if (response.data.success) {
        // 업로드 중인 파일 목록에 추가
        const uploadingFile = {
          file_id: response.data.file_id,
          file_name: response.data.file_name,
          s3_path: response.data.s3_path,
          status: 'pending',
          progress: 0,
          error_message: null
        };
        commit('ADD_UPLOADING_FILE', { sessionId, file: uploadingFile });
        
        console.log(`파일 업로드 완료, ID: ${response.data.file_id}, 세션: ${sessionId}`); // 디버깅용 로그
        
        // 2초 딜레이 후 상태 추적 시작 (DB 반영 시간 고려)
        setTimeout(() => {
          dispatch('startFileStatusTracking', { fileId: response.data.file_id, sessionId });
        }, 2000);
      }
      
      return response;
    } catch (error) {
      console.error('파일 업로드 실패:', error);
      throw error;
    }
  },

  async getFileStatus({ commit }, fileId) {
    try {
      const response = await api.get(`/api/v1/file/status/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('파일 상태 조회 실패:', error);
      throw error;
    }
  },

  startFileStatusTracking({ commit, dispatch }, { fileId, sessionId }) {
    // 기존 폴링이 있다면 정리
    commit('CLEAR_POLLING_INTERVAL', fileId);
    
    const interval = setInterval(async () => {
      try {
        const status = await dispatch('getFileStatus', fileId);
        
        // 업로드 중인 파일 상태 업데이트
        commit('UPDATE_UPLOADING_FILE', {
          sessionId,
          fileId: status.file_id,
          status: status.status,
          progress: status.progress,
          errorMessage: status.error_message
        });
        
        // 완료 또는 실패 시 폴링 중단
        if (status.status === 'completed' || status.status === 'failed') {
          commit('CLEAR_POLLING_INTERVAL', fileId);
          
          // 3초 후 업로드 중인 파일 목록에서 제거
          setTimeout(() => {
            commit('REMOVE_UPLOADING_FILE', { sessionId, fileId });
          }, 3000);
        }
        
      } catch (error) {
        console.error('파일 상태 추적 중 오류:', error);
        commit('CLEAR_POLLING_INTERVAL', fileId);
      }
    }, 5000); // 5초마다 폴링
    
    commit('SET_POLLING_INTERVAL', { fileId, interval });
  },

  stopFileStatusTracking({ commit }, fileId) {
    commit('CLEAR_POLLING_INTERVAL', fileId);
  },

  stopAllFileStatusTracking({ commit }) {
    commit('CLEAR_ALL_POLLING_INTERVALS');
  },

  // 파일 다운로드를 위한 서명된 URL 가져오기
  async getFileDownloadUrl({ commit }, filePath) {
    commit('SET_DOWNLOADING', true);
    commit('SET_DOWNLOAD_ERROR', null);
    
    try {
        console.log('filePath', filePath)
      const response = await api.get(`/api/v1/download/download-file?file_path=${encodeURIComponent(filePath)}`);
      commit('SET_DOWNLOADING', false);
      return response.data; // { url: '서명된 URL', expires_in: 3600 }
    } catch (error) {
      commit('SET_DOWNLOADING', false);
      commit('SET_DOWNLOAD_ERROR', error.response?.data?.detail || '파일 다운로드 URL을 가져오는 데 실패했습니다.');
      throw error;
    }
  },
  
  // 다운로드 링크 열기 또는 파일 다운로드
  async downloadFile({ dispatch }, filePath) {
    try {
      const { url } = await dispatch('getFileDownloadUrl', filePath);
      
      // 방법 1: 새 탭에서 URL 열기
      window.open(url, '_blank');
      
      // 방법 2: 파일 직접 다운로드 (옵션)
      /*
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filePath.split('/').pop();
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
      */
      
      return true;
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      return false;
    }
  },
  async getSessionFiles({ commit }, sessionId) {
    try {
      const response = await api.get(`/api/v1/file/session/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

const getters = {
  uploadingFiles: (state) => state.uploadingFiles,
  getUploadingFilesBySession: (state) => (sessionId) => {
    return state.uploadingFiles[sessionId] || [];
  },
  downloadingFile: (state) => state.downloadingFile,
  downloadError: (state) => state.downloadError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
